/**
 * Role Matrix permission model (client mirror of the API's lib/permissions.ts).
 *
 * Permissions are module × action pairs ("properties.edit") generated from the
 * dashboard sidebar. Each tenant role (Role Matrix) grants a set of them; a
 * managed member (builder_user / sales_rep) inherits the set from their
 * assigned role. Owners (tenant_admin / super_admin) carry NULL and are
 * treated as full-access. Keep this catalog in lockstep with the API's
 * PERMISSION_MODULES and the `permissions` table (migration 0077).
 */

export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export interface PermissionModule {
    key: string;
    label: string;
    actions: readonly PermissionAction[];
    /** Section heading the module renders under in the matrix (e.g. "Settings"). */
    group?: string;
    /** Master row of its group — rendered as the group heading with its own checkboxes. */
    parent?: boolean;
    /**
     * The page's own name, for prose outside the matrix (access-denied and
     * upgrade dialogs). "Assign Properties" is a matrix label describing a
     * SCOPE; the page a member was turned away from is just "Properties".
     */
    pageLabel?: string;
}

/** One entry per sidebar module, in sidebar order — the Role Matrix rows. */
export const PERMISSION_MODULES = [
    { key: 'dashboard',     label: 'Dashboard',         actions: ['view'] },
    // Properties / Leads are SCOPE pairs (migration 0087). The parent row is
    // the page plus its actions, seen through the assigned-only filter — the
    // member works the records handed to them one at a time. The `all_*` child
    // is a single checkbox that lifts the filter to the whole workspace.
    { key: 'properties',     label: 'Assign Properties', actions: ['view', 'create', 'edit', 'delete'], group: 'Properties', parent: true, pageLabel: 'Properties' },
    { key: 'all_properties', label: 'All Properties',    actions: ['view'],                             group: 'Properties' },
    { key: 'builders',      label: 'Builders',          actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'sales_team',    label: 'Sales Team',        actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'leads',         label: 'Assign Leads',      actions: ['view', 'create', 'edit', 'delete'], group: 'Leads', parent: true, pageLabel: 'Leads' },
    { key: 'all_leads',     label: 'All Leads',         actions: ['view'],                             group: 'Leads' },
    { key: 'qr_codes',      label: 'QR Codes',          actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'compare',       label: 'Compare',           actions: ['view'] },
    { key: 'analytics',     label: 'Analytics',         actions: ['view'] },
    { key: 'web_analytics', label: 'Website Analytics', actions: ['view'] },
    { key: 'billing',       label: 'Billing',           actions: ['view', 'edit'] },
    { key: 'support',       label: 'Support',           actions: ['view', 'create'] },
    { key: 'notifications', label: 'Notifications',     actions: ['view'] },
    // Workspace Settings — the parent `settings` row is the master switch for
    // the whole area (migration 0080); the per-section modules below decide
    // which sections are usable inside it, in settings-hub order.
    { key: 'settings',                  label: 'Settings',                 actions: ['view'],                             group: 'Settings', parent: true },
    { key: 'settings_profile',          label: 'Profile',                  actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_two_factor',       label: 'Two-Factor Auth (2FA)',    actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_branding',         label: 'Branding & White-label',   actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_integrations',     label: 'Integrations',             actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_sending_identity', label: 'Sending Identity',         actions: ['view', 'create', 'edit', 'delete'], group: 'Settings' },
    { key: 'settings_lead_routing',     label: 'Lead Routing',             actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'role_matrix',               label: 'Role Matrix',              actions: ['view', 'create', 'edit', 'delete'], group: 'Settings' },
    { key: 'settings_welcome_sequence', label: 'Welcome Sequence',         actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_notifications',    label: 'Notification Preferences', actions: ['view', 'edit'],                     group: 'Settings' },
    { key: 'settings_referrals',        label: 'Referral Program',         actions: ['view'],                             group: 'Settings' },
    { key: 'settings_terms',            label: 'Terms & Conditions',       actions: ['view'],                             group: 'Settings' },
] as const satisfies ReadonlyArray<PermissionModule>;

export type ModuleKey = (typeof PERMISSION_MODULES)[number]['key'];

/**
 * A permission key is "<module>.<action>". The template type admits pairs the
 * catalog doesn't define (e.g. "dashboard.delete"); `isPermissionKey` is the
 * runtime authority.
 */
export type PermissionKey = `${ModuleKey}.${PermissionAction}`;

/**
 * Plan-gated action keys that live outside the matrix (export buttons). For
 * managed members these resolve to the module's `view` grant; for owners they
 * hit the plan gates.
 */
export type PlanActionKey = 'analytics.export' | 'leads.export';

/** Anything the access layer can be asked about. */
export type AccessKey = PermissionKey | PlanActionKey;

/**
 * Granted-permission map for a member: "module.action" → true. NULL (absence)
 * means owner / full access.
 */
export type PermissionMap = Record<string, boolean>;

export function permissionKey(module: ModuleKey, action: PermissionAction): PermissionKey {
    return `${module}.${action}`;
}

/**
 * Scope-widener module → the page module it widens (migration 0087). Mirrors
 * the API's SCOPE_PARENT.
 *
 * "All Properties" without "Assign Properties" would widen a page the member
 * can't open, so ticking a widener implies its parent's View; clearing the
 * parent's View clears the widener with it.
 */
export const SCOPE_PARENT: Record<string, ModuleKey> = {
    all_properties: 'properties',
    all_leads:      'leads',
};

/** The wideners a page module owns ("properties" → ["all_properties"]). */
export function scopeChildrenOf(module: string): ModuleKey[] {
    return Object.entries(SCOPE_PARENT)
        .filter(([, parent]) => parent === module)
        .map(([child]) => child as ModuleKey);
}

/** Every valid key the catalog defines, in matrix display order. */
export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_MODULES.flatMap(
    (m) => m.actions.map((a) => permissionKey(m.key, a)),
);

const VALID_KEYS = new Set<string>(PERMISSION_KEYS);

export function isPermissionKey(key: string): key is PermissionKey {
    return VALID_KEYS.has(key);
}

/** The module half of an access key ("properties.edit" → "properties"). */
export function moduleOf(key: string): string {
    const i = key.lastIndexOf('.');
    return i === -1 ? key : key.slice(0, i);
}

/**
 * Flip one matrix checkbox, keeping the catalog's invariants — shared by the
 * Role Matrix editor and the per-user Permission Checklist so the two can't
 * drift from each other or from the API's normalizePermissionKeys:
 *
 *   - any grant implies the module's View (you can't act on a page you can't open)
 *   - clearing View revokes the whole module (the page itself is gone)
 *   - a scope widener implies its parent page's View, and dies with it
 */
export function togglePermission(
    granted: ReadonlySet<string>,
    module: ModuleKey,
    action: PermissionAction,
): Set<string> {
    const key = permissionKey(module, action);
    const next = new Set(granted);
    if (next.has(key)) {
        next.delete(key);
        if (action === 'view') {
            for (const k of [...next]) if (moduleOf(k) === module) next.delete(k);
            // Losing the page takes its scope wideners with it — "All Leads" is
            // meaningless once "Assign Leads" is off.
            for (const child of scopeChildrenOf(module)) {
                for (const k of [...next]) if (moduleOf(k) === child) next.delete(k);
            }
        }
    } else {
        next.add(key);
        next.add(permissionKey(module, 'view'));
        const parent = SCOPE_PARENT[module];
        if (parent) next.add(permissionKey(parent, 'view'));
    }
    return next;
}

const ACTION_LABEL: Record<string, string> = {
    view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', export: 'Export',
};

/**
 * Human label for an access key — "properties.create" → "Create Properties".
 * Uses the module's `pageLabel` where the matrix label describes a scope rather
 * than the page ("Assign Properties" → "Properties").
 */
export function permissionLabel(key: string): string | null {
    const module = PERMISSION_MODULES.find((m) => m.key === moduleOf(key)) as MatrixModule | undefined;
    if (!module) return null;
    const name = module.pageLabel ?? module.label;
    const action = ACTION_LABEL[key.slice(key.lastIndexOf('.') + 1)];
    return action ? `${action} ${name}` : name;
}

/**
 * True for keys that additionally require the parent "Settings → View" master
 * permission: every settings section, plus the Role Matrix (reached through
 * Settings). With the parent off, none of them is accessible regardless of the
 * per-section grants. Mirrors the API's requiresSettingsParent.
 */
export function requiresSettingsParent(key: string): boolean {
    return key.startsWith('settings_') || key.startsWith('role_matrix.');
}

/** Granted-key count for a role — the Role Matrix card badge. */
export function countGranted(perms: PermissionMap | string[]): number {
    if (Array.isArray(perms)) return perms.filter((k) => VALID_KEYS.has(k)).length;
    return PERMISSION_KEYS.filter((k) => perms[k]).length;
}

export const TOTAL_PERMISSIONS = PERMISSION_KEYS.length;

/**
 * A matrix row as the editors consume it — same shape as PermissionModule, but
 * with the key narrowed to the catalog's union so `permissionKey()` accepts it,
 * and `group`/`parent` readable without a cast (the const-narrowed catalog
 * entries omit them individually).
 */
export interface MatrixModule extends PermissionModule {
    key: ModuleKey;
}

/**
 * The matrix rows a workspace should offer.
 *
 * "Builders" is a Developer concept — a Builder or Realtor workspace has no
 * builder partners of its own, so offering the row there would let an admin
 * grant a permission that can never mean anything. Filtering it out keeps the
 * matrix honest about what the workspace actually has. The API drops the same
 * keys on write (keysForWorkspace).
 */
export function modulesForWorkspace(isDeveloperWorkspace: boolean): readonly MatrixModule[] {
    if (isDeveloperWorkspace) return PERMISSION_MODULES;
    return PERMISSION_MODULES.filter((m) => m.key !== 'builders');
}

/** Total grantable keys for the modules a workspace offers (the card's "x/y"). */
export function totalPermissionsFor(modules: readonly MatrixModule[]): number {
    return modules.reduce((n, m) => n + m.actions.length, 0);
}
